import { z } from 'zod';

import { customerRefSchema } from '@/lib/api/customer-ref';
import { pageMetaSchema } from '@/lib/api/pagination';

/** The six Order Statuses (CONTEXT.md "Order status"), in lifecycle order. `payment_processing` /
 *  `payment_failed` are the pre-placement statuses (Pending Confirmation / Failed, feature 122355). */
export const orderStatusSchema = z.enum([
  'payment_processing',
  'payment_failed',
  'order_placed',
  'shipped',
  'delivered',
  'cancelled',
]);

export const orderLineItemKindSchema = z.enum(['device', 'component', 'bundle']);

export const orderBundleDeviceSchema = z.object({
  name: z.string(),
  quantity: z.number(),
});

// `itemType` is the published wire field name for a line item's class (device/component/bundle).
export const orderLineItemSchema = z.object({
  id: z.string(),
  itemType: orderLineItemKindSchema,
  name: z.string(),
  imageUrl: z.string().nullable(),
  quantity: z.number(),
  unitPrice: z.number(),
  installationFee: z.number().nullable(),
  bundleDevices: z.array(orderBundleDeviceSchema).optional(),
  /** Count of this line item's Units replaced via an **Approved** replacement request. Pending and
   *  Rejected never count — the rule is server-side. Frontend-proposed field (ADR-0022, FLAG-4 on
   *  story 112368): `.nullish()` so an order detail without it parses and renders no label. */
  replacedCount: z.number().nullish(),
});

export const orderReviewItemSchema = z.object({
  orderItemId: z.string(),
  productName: z.string(),
  imageUrl: z.string().nullable(),
  rating: z.number().min(1).max(5).nullable(),
  isRated: z.boolean(),
  ratedAt: z.string().nullable(),
});

export const orderReviewSchema = z.object({
  orderId: z.string(),
  items: z.array(orderReviewItemSchema),
  comment: z.string().nullable(),
  commentSubmittedAt: z.string().nullable(),
  commentUpdatedAt: z.string().nullable(),
  commentIsEdited: z.boolean(),
  isComplete: z.boolean(),
});

/**
 * The linked installation request's **Installation status** (CONTEXT.md: Open / In Progress /
 * Rejected / Completed).
 *
 * Deliberately *not* the Replacement/Claim set (`pending`/`approved`/`rejected`/`cancelled`) this
 * enumerated until ADO 117331: staging returned `{"status":"open"}` on a freshly-placed order, the
 * parse failed, and `parseContract` threw — taking the entire order-detail page down to "Something
 * went wrong". Only `rejected` overlapped the two sets, which is why older orders opened fine and the
 * bug first came back "Unable to Reproduce".
 *
 * Duplicated from `features/requests`' `installationStatusSchema` rather than imported: a *value*
 * import from `@/features/requests` closes a module cycle back through this file, so the enum
 * evaluates as `undefined` at load time and every parse throws.
 */
export const installationRequestStatusSchema = z.enum([
  'open',
  'in_progress',
  'rejected',
  'completed',
]);

export const orderDetailSchema = z.object({
  id: z.string(),
  orderNumber: z.string(),
  // ADO 117767 — the Download Invoice control is rendered from `invoiceId !== null`, never from
  // `status`: an order cancelled after a successful payment keeps its invoice (BE rule 8). Null
  // for an order with no successful payment, and for one placed before invoicing shipped.
  invoiceId: z.string().nullable(),
  invoiceNumber: z.string().nullable(),
  status: orderStatusSchema,
  customer: customerRefSchema.extend({ email: z.string().nullable() }),
  lineItems: z.array(orderLineItemSchema),
  totals: z.object({
    orderPrice: z.number(),
    installationFees: z.number(),
    tax: z.number(),
    total: z.number(),
    // ADO 120987 — the stored `total` is already net of any discount, so the summary must list it
    // to add up. Not yet in the backend's `OrderDetailResponse`: absent or null parses as "no
    // discount" rather than failing the page, and the row appears the moment the backend sends it.
    // Proposed BE shape: `discount: Number(order.discountAmount)`, `discountName: order.discountName`.
    discount: z
      .number()
      .nullish()
      .transform((v) => v ?? 0),
    discountName: z
      .string()
      .nullish()
      .transform((v) => v ?? null),
  }),
  orderMeta: z.object({
    // Null until the payment succeeds: the backend sends `placedAt`, which a pre-placement order
    // does not have (feature 122355). Renders "-" through `formatOrderDate`.
    orderDate: z.string().nullable(),
    // ADO 118734 — both are `string | null` in the backend's own `OrderDetailResponse`, and both
    // are null on every **replacement** order: it is created with zero amounts and no Stripe
    // charge, so `paymentReference` (the Transaction ID) and the card brand/last4 behind
    // `paymentMethod` are never set. Requiring a string here took the whole page down to
    // "Something went wrong" via `parseContract` — the same failure mode ADO 117331 hit above.
    // Only the Swagger example (`order.constants.ts`) ever showed them as plain strings.
    paymentMethod: z.string().nullable(),
    paymentDate: z.string().nullable(),
    paymentComplete: z.boolean(),
    transactionId: z.string().nullable(),
    expectedDelivery: z.string().nullable(),
  }),
  logistics: z.object({
    city: z.string(),
    billingAddress: z.string(),
    shippingAddress: z.string(),
  }),
  installationRequest: z.object({ status: installationRequestStatusSchema }).nullable(),
  deliveredAt: z.string().nullable(),
});

export const orderRowSchema = z.object({
  id: z.string(),
  orderNumber: z.string(),
  customer: customerRefSchema,
  orderAmount: z.number(),
  deviceCount: z.number(),
  status: orderStatusSchema,
  orderedAt: z.string().nullable(),
  deliveredAt: z.string().nullable(),
  city: z.string(),
});

export const orderListResponseSchema = z.object({
  data: z.array(orderRowSchema),
  meta: pageMetaSchema,
});

export const orderCustomerOptionSchema = z.object({ id: z.string(), name: z.string() });

/** One page of the cursor-paginated customer typeahead. `nextCursor` (opaque
 *  `base64(name + customerId)`) is present only when `hasNextPage` is true. */
export const orderCustomerPageSchema = z.object({
  data: z.array(orderCustomerOptionSchema),
  meta: z.object({
    nextCursor: z.string().optional(),
    hasNextPage: z.boolean(),
  }),
});

export const orderFiltersSchema = z
  .object({
    customerIds: z.array(z.string()),
    cities: z.array(z.string()),
    statuses: z.array(orderStatusSchema),
    orderedFrom: z.string().nullable(),
    orderedTo: z.string().nullable(),
    amountFrom: z.number().nullable(),
    amountTo: z.number().nullable(),
    deviceCountFrom: z.number().nullable(),
    deviceCountTo: z.number().nullable(),
  })
  .superRefine((v, ctx) => {
    // No of Devices: whole numbers 1-100,000 (story 102118 §8).
    for (const key of ['deviceCountFrom', 'deviceCountTo'] as const) {
      const n = v[key];
      if (typeof n !== 'number') continue;
      if (n < 0) {
        ctx.addIssue({ code: 'custom', path: [key], message: 'No of Devices cannot be negative' });
      } else if (n < 1) {
        ctx.addIssue({ code: 'custom', path: [key], message: 'No of Devices must be at least 1' });
      } else if (n > 100_000) {
        ctx.addIssue({
          code: 'custom',
          path: [key],
          message: 'No of Devices cannot exceed 100,000',
        });
      }
    }
  })
  .superRefine((v, ctx) => {
    // Order Amount: cap at 999,999,999.99 (story 102118 §7).
    for (const key of ['amountFrom', 'amountTo'] as const) {
      const n = v[key];
      if (typeof n === 'number' && n > 999_999_999.99) {
        ctx.addIssue({
          code: 'custom',
          path: [key],
          message: 'Order Amount cannot exceed 999,999,999.99',
        });
      }
    }
  })
  .superRefine((v, ctx) => {
    // From > To range guards, each with its field-specific copy (story 102118 §7/§8).
    if (
      typeof v.amountFrom === 'number' &&
      typeof v.amountTo === 'number' &&
      v.amountFrom > v.amountTo
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['amountTo'],
        message: 'From amount should not be greater than To amount',
      });
    }
    if (
      typeof v.deviceCountFrom === 'number' &&
      typeof v.deviceCountTo === 'number' &&
      v.deviceCountFrom > v.deviceCountTo
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['deviceCountTo'],
        message: 'From value should not be greater than To value',
      });
    }
    if (v.orderedFrom && v.orderedTo && v.orderedFrom > v.orderedTo) {
      ctx.addIssue({
        code: 'custom',
        path: ['orderedTo'],
        message: 'From date cannot be after To date',
      });
    }
  });

export const orderAssigneeSchema = z.object({ id: z.string(), name: z.string() });

export const raiseTicketSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  assigneeId: z.string().min(1, 'Choose an assignee'),
  description: z.string().min(1, 'Description is required'),
});

/** Injects the AC "hidden fields" (Order ID + Ticket Type) into the POST body. */
export function toTicketBody(orderId: string, values: z.infer<typeof raiseTicketSchema>) {
  return { ...values, orderId, ticketType: 'order' as const };
}
